import { type FC } from 'react'
import classNames from 'classnames'
import { type AccountCustomWorkflowRunSummary, type CustomWorkflowSummary } from '../../../store/customWorkflow/customWorkflows'
import { parseWorkflowTags } from '../../../components/CustomWorkflows/workflowTags'
import { CategoryIcon } from '../../../components/CustomWorkflows/tags/CategoryIcon'
import { CategoryBadge } from '../../../components/CustomWorkflows/tags/CategoryBadge'
import { KindBadge } from '../../../components/CustomWorkflows/tags/KindBadge'
import { HashtagChips } from '../../../components/CustomWorkflows/tags/HashtagChips'
import { WorkflowRunStatusBadge } from '../../../components/CustomWorkflows/WorkflowRunStatusBadge'
import { RunContextChip } from '../../../components/CustomWorkflows/runContext/RunContextChip'
import { Button } from '../../../components/Button/Button'
import { type RunRowAction } from './WorkflowRunRow'
import { formatRunTimestamp } from '../../../components/CustomWorkflows/formatRunTimestamp'
import { formatRunDuration } from '../../../components/CustomWorkflows/formatRunDuration'
import { WorkflowTile } from '../Library/WorkflowTile'

interface RunTileProps {
  run: AccountCustomWorkflowRunSummary
  workflow?: CustomWorkflowSummary
  action?: RunRowAction
  showSubRunBadge?: boolean
  expanded?: boolean
  index?: number
  className?: string
  onToggleExpand?: () => void
}

export const RunTile: FC<RunTileProps> = ({ run, workflow, action, showSubRunBadge, expanded, index, className, onToggleExpand }) => {
  const parsed = workflow ? parseWorkflowTags(workflow) : null
  const childCount = run.childCount ?? 0
  const expandable = childCount > 0 && !!onToggleExpand
  return (
    <WorkflowTile index={index} className={className}>
      <div className='d-flex align-items-start justify-content-between gap-2 w-100'>
        <CategoryIcon category={parsed?.category ?? null} />
        <div className='d-flex flex-wrap gap-1 justify-content-end'>
          {parsed ? (
            <>
              <KindBadge kind={parsed.kind} />
              <CategoryBadge category={parsed.category} />
            </>
          ) : null}
          {showSubRunBadge && run.isSubRun ? (
            <span className='paragraph-extra-small medium zocks-px-0.75 zocks-py-0.125 rounded-2 zocks-bg-color-gray-100 zocks-text-gray-600 flex-shrink-0'>
              Sub-run
            </span>
          ) : null}
        </div>
      </div>
      <div className='paragraph-small medium text-truncate w-100'>{run.workflowName || run.workflowId}</div>
      <RunContextChip context={run.context} />
      <div className='paragraph-small zocks-text-gray-600'>
        Started {formatRunTimestamp(run.createdAt)}
        {run.elapsedTime !== null ? ` · ${formatRunDuration(run.elapsedTime)}` : run.finishedAt ? ` · Finished ${formatRunTimestamp(run.finishedAt)}` : ''}
      </div>
      {parsed ? <HashtagChips tags={parsed.hashtags} max={5} /> : null}
      <div className='mt-auto w-100 pt-2'>
        <div className='pt-3 border-top zocks-border-color-gray-200 d-flex justify-content-between align-items-center gap-2'>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <WorkflowRunStatusBadge status={run.status} />
            {expandable ? (
              <Button type='button' variant='borderless' className='px-2 py-1' onClick={onToggleExpand} aria-expanded={expanded}>
                <span className='paragraph-extra-small medium d-flex align-items-center zocks-gap-1'>
                  <i className={classNames('bi', expanded ? 'bi-chevron-down' : 'bi-chevron-right')} aria-hidden='true' />
                  {childCount} sub-run{childCount === 1 ? '' : 's'}
                </span>
              </Button>
            ) : null}
          </div>
          {action ? (
            <Button type='button' variant={action.variant} className='px-2 py-1 rounded-2' onClick={action.onClick}>
              <span className='paragraph-extra-small medium d-inline-flex align-items-center gap-1'>
                {action.label}
                <i className='bi bi-arrow-right' aria-hidden='true' />
              </span>
            </Button>
          ) : null}
        </div>
      </div>
    </WorkflowTile>
  )
}
